import React from 'react';
import { Checkbox, NumberInput, Select, SelectItem, TextArea, TextInput } from '@carbon/react';
import { ISchedulerProfile } from '../../../AppSlice';

export interface AdvancedConfig {
  taskType: string;
  input: string;
  inputType: string;
  inputResourceId: string;
  output: string;
  outputType: string;
  outputResourceId: string;
  additionalOutputEnabled: boolean;
  additionalOutputResourceId: string;
  additionalOutputProfile: string;
  filename: string;
  profile: string;
  realtime: boolean;
  additionalArgs: string;
  heartbeatFile: string;
  heartbeatIntervalMs: number;
  heartbeatMessage: string;
}

export interface AdvancedScheduleConfig {
  enabled: boolean;
  tz: string;
  overlapPolicy: string;
  misfirePolicy: string;
  retryMaxAttempts: number;
  retryInitialMs: number;
  retryBackoff: string;
}

export interface SdiResource {
  id: string;
  label: string;
  config?: Record<string, any> | null;
}

export interface FilesystemResource {
  id: string;
  label: string;
  path?: string;
}

export interface RtmpResource {
  id: string;
  label: string;
  url?: string;
}

export const emptyAdvancedConfig = (): AdvancedConfig => ({
  taskType: 'MEDIAENGINE_RECORD',
  input: '',
  inputType: 'rtmp',
  inputResourceId: '',
  output: '',
  outputType: 'file',
  outputResourceId: '',
  additionalOutputEnabled: false,
  additionalOutputResourceId: '',
  additionalOutputProfile: '',
  filename: '',
  profile: '',
  realtime: true,
  additionalArgs: '',
  heartbeatFile: 'C:\\temp\\slotA_default.txt',
  heartbeatIntervalMs: 1000,
  heartbeatMessage: 'slotA_default',
});

export const emptyAdvancedScheduleConfig = (): AdvancedScheduleConfig => ({
  enabled: true,
  tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  overlapPolicy: 'SKIP_IF_RUNNING',
  misfirePolicy: 'START_IF_BEFORE_END',
  retryMaxAttempts: 3,
  retryInitialMs: 1000,
  retryBackoff: 'exponential',
});

export const buildAdvancedConfigParams = (config: AdvancedConfig): Record<string, any> => {
  if (config.taskType === 'DUMMY_HEARTBEAT') {
    return {
      file: config.heartbeatFile.trim(),
      intervalMs: Number.isFinite(config.heartbeatIntervalMs) ? config.heartbeatIntervalMs : 1000,
      message: config.heartbeatMessage.trim(),
    };
  }

  if (config.taskType === 'MEDIAENGINE_RECORD') {
    const isSdi = config.inputType === 'sdi';
    const inputValue = isSdi && config.inputResourceId
      ? { id: config.inputResourceId }
      : { type: config.inputType, url: config.input.trim() };

    const outputValue = config.outputResourceId
      ? { id: config.outputResourceId }
      : { type: 'file', url: config.output.trim() };

    const result: Record<string, any> = {
      input: inputValue,
      output: outputValue,
      profile: config.profile.trim(),
      realtime: config.realtime,
      additionalArgs: config.additionalArgs.split(/\r?\n/).map((l) => l.trim()).filter(Boolean),
    };

    if (config.additionalOutputEnabled && config.additionalOutputResourceId) {
      result.additionalOutputs = [{
        output: { id: config.additionalOutputResourceId },
        profile: config.additionalOutputProfile.trim(),
      }];
    }

    if (config.filename.trim()) {
      result.filename = config.filename.trim();
    }

    return result;
  }

  return {
    input: config.input.trim(),
    output: config.output.trim(),
    realtime: config.realtime,
    additionalArgs: config.additionalArgs.split(/\r?\n/).map((l) => l.trim()).filter(Boolean),
  };
};

export const parseAdvancedConfig = (
  taskType: string,
  params: Record<string, any>,
): AdvancedConfig => {
  const defaults = emptyAdvancedConfig();
  const p = params ?? {};

  if (taskType === 'DUMMY_HEARTBEAT') {
    return {
      ...defaults,
      taskType,
      heartbeatFile: (p.file ?? defaults.heartbeatFile).toString(),
      heartbeatIntervalMs: Number.isFinite(Number(p.intervalMs)) ? Number(p.intervalMs) : defaults.heartbeatIntervalMs,
      heartbeatMessage: (p.message ?? defaults.heartbeatMessage).toString(),
    };
  }

  const inputObj = p.input;
  let input = '';
  let inputType = 'rtmp';
  let inputResourceId = '';

  if (inputObj && typeof inputObj === 'object' && !Array.isArray(inputObj)) {
    if (inputObj.id) {
      inputResourceId = inputObj.id.toString();
      inputType = 'sdi';
    } else {
      input = (inputObj.url ?? inputObj.inputUrl ?? '').toString();
      inputType = (inputObj.type ?? 'rtmp').toString();
    }
  } else if (typeof inputObj === 'string') {
    input = inputObj;
  } else {
    input = (p.inputUrl ?? '').toString();
  }

  if ((inputType === 'rtmp' || !inputType) && input.toLowerCase().startsWith('sdi://')) {
    inputType = 'sdi';
  }

  const outputObj = p.output;
  let output = defaults.output;
  let outputType = 'file';
  let outputResourceId = '';

  if (outputObj && typeof outputObj === 'object' && !Array.isArray(outputObj)) {
    if (outputObj.id) {
      outputResourceId = outputObj.id.toString();
    } else {
      output = (outputObj.url ?? defaults.output).toString();
      outputType = (outputObj.type ?? 'file').toString();
    }
  } else if (typeof outputObj === 'string') {
    output = outputObj || defaults.output;
  }

  const additionalOutputConfig = Array.isArray(p.additionalOutputs)
    ? p.additionalOutputs[0]
    : p.additionalOutput;
  const additionalOutputResourceId = (
    additionalOutputConfig?.output?.id ??
    additionalOutputConfig?.id ??
    p.additionalOutputResourceId ??
    ''
  ).toString();
  const additionalOutputProfile = (
    additionalOutputConfig?.profile ??
    p.additionalOutputProfile ??
    ''
  ).toString();

  return {
    taskType,
    input,
    inputType,
    inputResourceId,
    output,
    outputType,
    outputResourceId,
    additionalOutputEnabled: Boolean(additionalOutputResourceId),
    additionalOutputResourceId,
    additionalOutputProfile,
    filename: (p.filename ?? '').toString(),
    profile: (p.profile ?? '').toString(),
    realtime: typeof p.realtime === 'boolean' ? p.realtime : true,
    additionalArgs: Array.isArray(p.additionalArgs) ? p.additionalArgs.join('\n') : '',
    heartbeatFile: (p.file ?? defaults.heartbeatFile).toString(),
    heartbeatIntervalMs: Number.isFinite(Number(p.intervalMs)) ? Number(p.intervalMs) : defaults.heartbeatIntervalMs,
    heartbeatMessage: (p.message ?? defaults.heartbeatMessage).toString(),
  };
};

const TASK_TYPES = ['DUMMY_HEARTBEAT', 'MEDIAENGINE_RECORD', 'FFMPEG_RECORD'];
const INPUT_TYPE_OPTIONS = ['sdi', 'ndi', 'rtmp'];
const OUTPUT_TYPE_OPTIONS = ['file', 'url'];
const OVERLAP_POLICIES = ['SKIP_IF_RUNNING', 'ALLOW', 'CANCEL_PREVIOUS', 'QUEUE'];
const MISFIRE_POLICIES = ['DO_NOTHING', 'FIRE_AND_PROCEED', 'START_IF_BEFORE_END'];
const RETRY_BACKOFF_OPTIONS = ['exponential', 'linear', 'constant'];

interface Props {
  value: AdvancedConfig;
  onChange: (updates: Partial<AdvancedConfig>) => void;
  sdiResources?: SdiResource[];
  filesystemResources?: FilesystemResource[];
  rtmpResources?: RtmpResource[];
  mediaengineProfiles?: ISchedulerProfile[];
  showFilename?: boolean;
  readOnly?: boolean;
  scheduleConfig?: AdvancedScheduleConfig;
  onScheduleConfigChange?: (updates: Partial<AdvancedScheduleConfig>) => void;
  showScheduleConfig?: boolean;
  scheduleReadOnly?: boolean;
  idPrefix?: string;
}

const RecorderAdvancedConfigForm: React.FC<Props> = ({
  value,
  onChange,
  sdiResources = [],
  filesystemResources = [],
  rtmpResources = [],
  mediaengineProfiles = [],
  showFilename = true,
  readOnly = false,
  scheduleConfig,
  onScheduleConfigChange,
  showScheduleConfig,
  scheduleReadOnly,
  idPrefix = 'adv-cfg',
}) => {
  const id = (key: string) => `${idPrefix}-${key}`;
  const isHeartbeat = value.taskType === 'DUMMY_HEARTBEAT';
  const isMediaengine = value.taskType === 'MEDIAENGINE_RECORD';
  const isSdi = isMediaengine && value.inputType === 'sdi';
  const selectedFs = filesystemResources.find((r) => r.id === value.outputResourceId);
  const selectedRtmp = rtmpResources.find((r) => r.id === value.additionalOutputResourceId);
  const resolvedScheduleConfig = scheduleConfig ?? emptyAdvancedScheduleConfig();
  const shouldShowScheduleConfig = showScheduleConfig ?? Boolean(scheduleConfig || onScheduleConfigChange);
  const isScheduleReadOnly = scheduleReadOnly ?? readOnly;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Select
        id={id('task-type')}
        labelText="Task Type"
        value={value.taskType}
        disabled={readOnly}
        onChange={(e) => onChange({ taskType: e.target.value })}
      >
        {TASK_TYPES.map((t) => <SelectItem key={t} value={t} text={t} />)}
      </Select>

      {isHeartbeat && (
        <>
          <TextInput
            id={id('hb-file')}
            labelText="File"
            value={value.heartbeatFile}
            readOnly={readOnly}
            onChange={(e) => onChange({ heartbeatFile: e.target.value })}
            placeholder="C:\\temp\\slotA_default.txt"
          />
          <TextInput
            id={id('hb-interval')}
            labelText="Interval (ms)"
            type="number"
            value={value.heartbeatIntervalMs.toString()}
            readOnly={readOnly}
            onChange={(e) => onChange({ heartbeatIntervalMs: Number(e.target.value) || 0 })}
          />
          <TextInput
            id={id('hb-message')}
            labelText="Message"
            value={value.heartbeatMessage}
            readOnly={readOnly}
            onChange={(e) => onChange({ heartbeatMessage: e.target.value })}
          />
        </>
      )}

      {!isHeartbeat && (
        <>
          {isMediaengine && (
            <Select
              id={id('input-type')}
              labelText="Input Type"
              value={value.inputType}
              disabled={readOnly}
              onChange={(e) =>
                onChange({ inputType: e.target.value, ...(e.target.value !== 'sdi' ? { inputResourceId: '' } : {}) })
              }
            >
              {INPUT_TYPE_OPTIONS.map((t) => (
                <SelectItem key={t} value={t} text={t.toUpperCase()} />
              ))}
            </Select>
          )}

          {isSdi ? (
            <Select
              id={id('sdi-source')}
              labelText="SDI Input Source"
              value={value.inputResourceId}
              disabled={readOnly}
              onChange={(e) => onChange({ inputResourceId: e.target.value })}
            >
              <SelectItem value="" text="Select SDI source" />
              {sdiResources.map((r) => (
                <SelectItem key={r.id} value={r.id} text={r.label} />
              ))}
            </Select>
          ) : (
            <TextInput
              id={id('input')}
              labelText="Input URL"
              value={value.input}
              readOnly={readOnly}
              onChange={(e) => onChange({ input: e.target.value })}
              placeholder={value.inputType === 'ndi' ? 'ndi://...' : 'rtmp://...'}
            />
          )}

          {isMediaengine ? (
            <>
              <div>
                <Select
                  id={id('output-resource')}
                  labelText="File Output Location"
                  value={value.outputResourceId}
                  disabled={readOnly}
                  onChange={(e) => onChange({ outputResourceId: e.target.value })}
                >
                  <SelectItem value="" text="Select output location" />
                  {filesystemResources.map((r) => (
                    <SelectItem key={r.id} value={r.id} text={r.label} />
                  ))}
                </Select>
                {selectedFs?.path && (
                  <div style={{ fontSize: 11, color: '#8d8d8d', marginTop: 4, paddingLeft: 2, fontFamily: 'monospace' }}>
                    {selectedFs.path}
                  </div>
                )}
              </div>

              {!value.outputResourceId && (
                <TextInput
                  id={id('output-url')}
                  labelText="Output URL"
                  helperText="Or select an output location above"
                  value={value.output}
                  readOnly={readOnly}
                  onChange={(e) => onChange({ output: e.target.value })}
                />
              )}

              {showFilename && (
                <TextInput
                  id={id('filename')}
                  labelText="Filename"
                  value={value.filename}
                  readOnly={readOnly}
                  onChange={(e) => onChange({ filename: e.target.value })}
                  placeholder="{timestamp}_{uuid}.mxf"
                  helperText="Placeholders: {timestamp}, {uuid}"
                />
              )}
            </>
          ) : (
            <>
              <Select
                id={id('output-type')}
                labelText="Output Type"
                value={value.outputType}
                disabled={readOnly}
                onChange={(e) => onChange({ outputType: e.target.value })}
              >
                {OUTPUT_TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t} text={t} />
                ))}
              </Select>
              <TextInput
                id={id('output')}
                labelText="Output URL"
                value={value.output}
                readOnly={readOnly}
                onChange={(e) => onChange({ output: e.target.value })}
              />
            </>
          )}

          {isMediaengine && (
            <>
              <Select
                id={id('profile')}
                labelText="Profile"
                value={value.profile}
                disabled={readOnly}
                onChange={(e) => onChange({ profile: e.target.value })}
              >
                <SelectItem value="" text="Select profile" />
                {mediaengineProfiles.map((p) => (
                  <SelectItem key={p.id} value={p.id} text={p.name} />
                ))}
              </Select>

              <Checkbox
                id={id('additional-output-enabled')}
                labelText="Additional RTMP output"
                checked={value.additionalOutputEnabled}
                disabled={readOnly}
                onChange={(_, { checked }) => onChange({
                  additionalOutputEnabled: Boolean(checked),
                  ...(!checked ? { additionalOutputResourceId: '', additionalOutputProfile: '' } : {}),
                })}
              />

              {value.additionalOutputEnabled && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 14, border: '1px solid #393939' }}>
                  <Select
                    id={id('additional-output-resource')}
                    labelText="RTMP Output Location"
                    value={value.additionalOutputResourceId}
                    disabled={readOnly}
                    onChange={(e) => onChange({ additionalOutputResourceId: e.target.value })}
                  >
                    <SelectItem value="" text="Select RTMP resource" />
                    {rtmpResources.map((r) => (
                      <SelectItem key={r.id} value={r.id} text={r.label} />
                    ))}
                  </Select>
                  {selectedRtmp?.url && (
                    <div style={{ fontSize: 11, color: '#8d8d8d', marginTop: -10, paddingLeft: 2, fontFamily: 'monospace' }}>
                      {selectedRtmp.url}
                    </div>
                  )}
                  <Select
                    id={id('additional-output-profile')}
                    labelText="RTMP Output Profile"
                    value={value.additionalOutputProfile}
                    disabled={readOnly}
                    onChange={(e) => onChange({ additionalOutputProfile: e.target.value })}
                  >
                    <SelectItem value="" text="Select profile" />
                    {mediaengineProfiles.map((p) => (
                      <SelectItem key={p.id} value={p.id} text={p.name} />
                    ))}
                  </Select>
                </div>
              )}
            </>
          )}

          <Checkbox
            id={id('realtime')}
            labelText="Realtime"
            checked={value.realtime}
            disabled={readOnly}
            onChange={(_, { checked }) => onChange({ realtime: Boolean(checked) })}
          />

          <TextArea
            id={id('additional-args')}
            labelText={isMediaengine ? 'Additional Args (one per line)' : 'Extra FFmpeg Arguments (one per line)'}
            rows={3}
            value={value.additionalArgs}
            readOnly={readOnly}
            onChange={(e) => onChange({ additionalArgs: e.target.value })}
          />
        </>
      )}

      {shouldShowScheduleConfig && (
        <>
          <Checkbox
            id={id('schedule-enabled')}
            labelText="Show Preview"
            checked={resolvedScheduleConfig.enabled}
            disabled={isScheduleReadOnly}
            onChange={(_, { checked }) => onScheduleConfigChange?.({ enabled: Boolean(checked) })}
          />

          <div style={{ display: 'flex', gap: 16 }}>
            <Select
              id={id('overlap-policy')}
              labelText="Overlap Policy"
              value={resolvedScheduleConfig.overlapPolicy}
              disabled={isScheduleReadOnly}
              onChange={(e) => onScheduleConfigChange?.({ overlapPolicy: e.target.value })}
            >
              {OVERLAP_POLICIES.map((policy) => (
                <SelectItem key={policy} value={policy} text={policy} />
              ))}
            </Select>
            <Select
              id={id('misfire-policy')}
              labelText="Misfire Policy"
              value={resolvedScheduleConfig.misfirePolicy}
              disabled={isScheduleReadOnly}
              onChange={(e) => onScheduleConfigChange?.({ misfirePolicy: e.target.value })}
            >
              {MISFIRE_POLICIES.map((policy) => (
                <SelectItem key={policy} value={policy} text={policy} />
              ))}
            </Select>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <NumberInput
              id={id('retry-max-attempts')}
              min={0}
              hideLabel={false}
              disabled={isScheduleReadOnly}
              value={resolvedScheduleConfig.retryMaxAttempts}
              label="Retry Max Attempts"
              onChange={(_, { value }) => onScheduleConfigChange?.({ retryMaxAttempts: Number(value) })}
            />
            <TextInput
              id={id('retry-initial-ms')}
              labelText="Retry Initial (ms)"
              type="number"
              readOnly={isScheduleReadOnly}
              value={resolvedScheduleConfig.retryInitialMs.toString()}
              onChange={(e) => onScheduleConfigChange?.({ retryInitialMs: Number(e.target.value) })}
            />
            <Select
              id={id('retry-backoff')}
              labelText="Retry Backoff"
              value={resolvedScheduleConfig.retryBackoff}
              disabled={isScheduleReadOnly}
              onChange={(e) => onScheduleConfigChange?.({ retryBackoff: e.target.value })}
            >
              {RETRY_BACKOFF_OPTIONS.map((option) => (
                <SelectItem key={option} value={option} text={option} />
              ))}
            </Select>
          </div>
        </>
      )}
    </div>
  );
};

export default RecorderAdvancedConfigForm;
